import React, { useState } from "react";
import { BRAND, getThemeColors } from "./themes";

const INDIVIDUAL_PLANS = [
  {
    name: "Free Trial",
    price: "$0",
    per: "",
    featured: false,
    features: ["20-message limit", "2 conversations", "Full scenario access", "Basic evaluation"],
  },
  {
    name: "Basic",
    price: "$15",
    per: "/month",
    featured: false,
    features: ["150 messages/month", "All basic features", "Full scenario library", "Standard evaluation"],
  },
  {
    name: "Premium",
    price: "$40",
    per: "/month",
    featured: true,
    features: [
      "Unlimited messages",
      "Custom personas",
      "Enhanced support",
      "Advanced analytics",
    ],
  },
];

const ORGANIZATION_PLANS = [
  {
    name: "Team",
    price: "$100",
    per: "/month",
    featured: false,
    features: ["Up to 20 users", "Unlimited access", "All basic features"],
  },
  {
    name: "Professional",
    price: "$400",
    per: "/month",
    featured: true,
    features: [
      "Up to 100 users",
      "Unlimited access",
      "Priority support",
      "Instructional videos",
    ],
  },
  {
    name: "Enterprise",
    price: "$2,000",
    per: "/month",
    featured: false,
    features: ["Up to 1,000 users", "Unlimited access", "Priority support", "Instructional videos"],
  },
];

function PlanCard({ plan, onSignUp }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      style={{
        background: '#ffffff',
        border: plan.featured ? `2px solid ${BRAND.primary}` : '1px solid #e0e0e0',
        borderRadius: 8,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 280,
        transition: 'all 0.3s',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : (plan.featured ? '0 4px 12px rgba(37, 87, 167, 0.15)' : 'none'),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        fontSize: 18,
        fontWeight: 600,
        color: '#1a1a1a',
        marginBottom: 8
      }}>
        {plan.name}
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 4,
        marginBottom: 16
      }}>
        <span style={{ fontSize: 32, fontWeight: 700, color: '#1a1a1a' }}>
          {plan.price}
        </span>
        <span style={{ fontSize: 14, color: '#666' }}>
          {plan.per}
        </span>
      </div>

      <ul style={{
        margin: 0,
        padding: '0 0 0 20px',
        fontSize: 14,
        color: '#333',
        lineHeight: 1.8,
        flex: 1
      }}>
        {plan.features.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>

      <button
        onClick={onSignUp}
        style={{
          marginTop: 20,
          padding: '10px 16px',
          borderRadius: 6,
          border: plan.featured ? 'none' : '1px solid #d0d0d0',
          background: plan.featured ? BRAND.primary : '#ffffff',
          color: plan.featured ? '#ffffff' : '#333',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (plan.featured) {
            e.currentTarget.style.background = '#1e4a8f';
          } else {
            e.currentTarget.style.background = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = plan.featured ? BRAND.primary : '#ffffff';
        }}
      >
        Get Started
      </button>
    </div>
  );
}

export default function LandingPage({ 
  onSignIn, 
  onSignUp, 
  styles, 
  theme, 
  isMobile,
  API_BASE_URL
}) {
  const [earlyAccessEmail, setEarlyAccessEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState(""); // "", "success", "error"
  const [pricingView, setPricingView] = useState('individual'); // 'individual' or 'organization'

  const handleEarlyAccess = async () => {
    if (!earlyAccessEmail || !earlyAccessEmail.includes("@")) {
      setSubmitStatus("error");
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/preregister`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: earlyAccessEmail })
      });
      const data = await res.json();
      
      if (data.success) {
        setSubmitStatus("success");
        setEarlyAccessEmail("");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  const darkThemeColors = getThemeColors('dark');

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fafafa',
      fontFamily: BRAND.font
    }}>
      <header style={{
        background: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/LBS-logo.png"
              alt="Lausanne Business Solutions"
              style={{
                height:'100%',
                maxHeight: 48,
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            gap: 12,
            marginLeft: 'auto',
            padding: '12px 12px'
          }}>
            <button onClick={onSignUp} style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: '1px solid #d0d0d0',
              background: '#ffffff',
              color: '#333',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}>
              Sign Up
            </button>
            <button onClick={onSignIn} style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              background: BRAND.primary,
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}>
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Early Access Banner */}
      <div style={{
        background: BRAND.error,
        color: '#ffffff',
        padding: isMobile ? '16px 12px' : '12px 24px',
        fontSize: isMobile ? 14 : 16,
        display: 'flex',
        flexDirection: isMobile? 'column':'row',
        alignItems: isMobile? 'flex-start':'center',
        gap: isMobile? 10:12,
        justifyContent: 'flex-start'
      }}>
        <span style={{ fontWeight: 600, whiteSpace: isMobile? 'normal':'nowrap' }}>
          Launching April 15! Sign up for early access:
        </span>
        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flex: 1,
          width: isMobile? '100%':'auto'
        }}>
          <input
            type="email"
            placeholder="your@email.com"
            value={earlyAccessEmail}
            onChange={(e) => setEarlyAccessEmail(e.target.value)}
            style={{
              padding: '4px 10px',
              borderRadius: 4,
              border: 'none',
              fontSize: isMobile? 14:16,
              flex: isMobile? 1:'none',
              width: isMobile? 'auto':200,
              minWidth: 0,
              boxSizing: 'border-box'
            }}
          />
          <button
            onClick={handleEarlyAccess}
            style={{
              padding: '4px 16px',
              borderRadius: 4,
              border: 'none',
              background: BRAND.canvas,
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            Sign Up
          </button>
        </div>
        {submitStatus === "success" && <span>✓ Signed up!</span>}
        {submitStatus === "error" && <span>✗ Invalid email</span>}
      </div>
      {/* Centered Logo */}
      <div style={{
        padding: '40px 24px 24px',
        background: '#fafafa',
        textAlign: 'center'
      }}>
        <img 
          src="/logo_wordmark.png" 
          alt="Loopen" 
          style={{ 
            height: isMobile ? 56 : 80,
            width: 'auto',
            objectFit: 'contain',
          }} 
        />
        <div style={{ 
          fontSize: 12, 
          color: '#666'
        }}>
          by Lausanne Business Solutions
        </div>
      </div>

      {/* Hero + Stats Combined */}
      <section style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)',
        borderBottom: '1px solid #e0e0e0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '48px 24px',
          display: 'grid',
          gridTemplateColumns: isMobile? '1fr':'1fr 1fr',
          gap: 48,
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              margin: '0 0 16px',
              fontSize: isMobile? 28:40,
              fontWeight: 700,
              color: '#1a1a1a',
              lineHeight: 1.2
            }}>
              Practice Management Feedback with AI
            </h1>
            <p style={{
              margin: '0 0 24px',
              fontSize: isMobile? 15:17,
              color: '#555',
              lineHeight: 1.6
            }}>
              Engage in realistic feedback conversations with AI personas. Receive detailed evaluations and actionable recommendations to improve your communication effectiveness.
            </p>
            
            {/* Inline stats */}
            <div style={{
              display: 'flex',
              gap: 32,
              marginBottom: 24
            }}>
              <div>
                <div style={{ fontSize: 32, fontWeight: 700, color: BRAND.primary }}>15+</div>
                <div style={{ fontSize: 13, color: '#666' }}>Scenarios</div>
              </div>
              <div>
                <div style={{ fontSize: 32, fontWeight: 700, color: BRAND.primary }}>9</div>
                <div style={{ fontSize: 13, color: '#666' }}>Categories</div>
              </div>
            </div>
            
            <button
              onClick={onSignUp}
              style={{
                padding: '12px 28px',
                borderRadius: 6,
                border: 'none',
                background: BRAND.primary,
                color: '#ffffff',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(37, 87, 167, 0.25)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Get Started
            </button>
          </div>

          {/* Product Screenshot */}
          <div style={{
            ...styles.card(darkThemeColors),
            height: 360,
            padding: 16
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={styles.avatar(44, BRAND.primary)}>JT</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: darkThemeColors.text }}>Jake Thompson</div>
                <div style={{ fontSize: 13, color: darkThemeColors.textMuted }}>Sales Account Executive • Sales Department</div>
              </div>
            </div>

            {/* Chat messages */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 8, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                <div style={{
                  maxWidth: '92%',
                  borderRadius: 18,
                  padding: '10px 14px',
                  background: '#1b1c1d',
                  color: darkThemeColors.textSecondary,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                  ...styles.chatText,
                }}>
                  I've been struggling with the deadline. Can we talk about what support I need?
                  <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>3:07 PM</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <div style={{
                  maxWidth: '92%',
                  borderRadius: 18,
                  padding: '10px 14px',
                  background: BRAND.primary,
                  color: '#fff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                  ...styles.chatText
                }}>
                  Let's break this down. What specific blockers did you face?
                  <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>3:08 PM</div>
                </div>
              </div>
            </div>

            {/* Input area */}
            <div>
              <textarea
                placeholder="Type your message here"
                disabled
                style={{
                  ...styles.chatText,
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: darkThemeColors.inputBg,
                  color: darkThemeColors.text,
                  border: `1px solid ${darkThemeColors.borderColor}`,
                  height: 48,
                  resize: 'vertical',
                  marginBottom: 8
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{
                    ...styles.sendBtn,
                    background: BRAND.spotlight,
                    fontSize: 14
                  }} disabled>
                    🎤 Start
                  </button>
                  <button style={{
                    ...styles.sendBtn,
                    background: BRAND.spotlight,
                    fontSize: 14
                  }} disabled>
                    🔊 Play Voice
                  </button>
                </div>
                <button style={{
                  ...styles.sendBtn,
                  fontSize: 14
                }} disabled>
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Compact */}
      <section style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '48px 24px'
      }}>
        <h2 style={{
          margin: '0 0 32px',
          fontSize: 32,
          fontWeight: 700,
          color: '#1a1a1a',
          textAlign: 'center'
        }}>
          How It Works
        </h2>
        <div style={{
          display: 'flex',
          flexDirection: isMobile? 'column':'row',
          gap: 16,
          justifyContent: 'space-between'
        }}>
          {[
            { num: 1, title: 'Select Scenario', desc: 'Choose from 15+ realistic workplace situations' },
            { num: 2, title: 'Practice', desc: 'AI responds with realistic personality-based behavior' },
            { num: 3, title: 'Get Evaluated', desc: 'Receive scoring across 9 categories with recommendations' }
          ].map((step) => (
            <div key={step.num} style={{
              flex: 1,
              background: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 20,
              minWidth: 0
            }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: BRAND.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
                color: '#fff',
                marginBottom: 12
              }}>{step.num}</div>
              <h3 style={{
                margin: '0 0 8px',
                fontSize: 14,
                fontWeight: 600,
                color: '#1a1a1a',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {step.title}
              </h3>
              <p style={{
                margin: 0,
                fontSize: 12,
                color: '#555',
                lineHeight: 1.5,
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

    {/* Why Loopen */}
      <section style={{
        background: 'f0f7ff',
        borderTop: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0',
        padding: '48px 24px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto'
        }}>
          <h2 style={{
            margin: '0 0 32px',
            fontSize: 32,
            fontWeight: 700,
            color: '#1a1a1a',
            textAlign: 'center'
          }}>
            Why Loopen?
          </h2>
          <p style={{
            margin: '0 auto 24px',
            fontSize: 16,
            color: '#333',
            lineHeight: 1.7,
            textAlign: 'left',
            maxWidth: 720
          }}>
            Communication training often costs <strong>$500–$1,500 per employee</strong>.
            1:1 coaching can exceed <strong>$250 per hour</strong>.
            Loopen delivers <strong>repeatable, on-demand practice and targeted feedback</strong>—at
            a fraction of the cost, and at a scale traditional training simply can't match.
          </p>
          <p style={{
            margin: '0 0 16px',
            fontSize: 16,
            color: '#333',
            fontWeight: 600,
            textAlign: 'center'
          }}>
            Drive measurable improvement with:
          </p>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 16,
            justifyContent: 'space-between'
          }}>
            {[
              'Realistic manager-employee simulations',
              'Immediate, structured feedback',
              'Continuous skill development—not one-time training'
            ].map((item, i) => (
              <div key={i} style={{
                flex: 1,
                background: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                padding: 20,
                minWidth: 0,
                fontSize: 15,
                color: '#333',
                lineHeight: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10
              }}>
                <span style={{
                  fontSize: 14,
                  color: BRAND.primary,
                  flexShrink: 0,
                  lineHeight: 1.5
                }}>★</span>
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing - Simplified */}
      <section style={{
        background: '#f8f9fa',
        borderTop: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0',
        padding: '48px 24px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto'
        }}>
          <h2 style={{
            margin: '0 0 24px',
            fontSize: 32,
            fontWeight: 700,
            color: '#1a1a1a',
            textAlign: 'center'
          }}>
            Simple Pricing
          </h2>

          {/* Toggle buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 12,
            marginBottom: 32
          }}>
            <button
              onClick={() => setPricingView('individual')}
              style={{
                padding: '10px 24px',
                borderRadius: 6,
                border: pricingView === 'individual' ? 'none' : '1px solid #d0d0d0',
                background: pricingView === 'individual' ? BRAND.primary : '#ffffff',
                color: pricingView === 'individual' ? '#ffffff' : '#333',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Individual
            </button>
            <button
              onClick={() => setPricingView('organization')}
              style={{
                padding: '10px 24px',
                borderRadius: 6,
                border: pricingView === 'organization' ? 'none' : '1px solid #d0d0d0',
                background: pricingView === 'organization' ? BRAND.primary : '#ffffff',
                color: pricingView === 'organization' ? '#ffffff' : '#333',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Organization
            </button>
          </div>

          {/* Pricing cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile? '1fr':'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 20,
            maxWidth: 900,
            margin: '0 auto'
          }}>
            {(pricingView === 'individual' ? INDIVIDUAL_PLANS : ORGANIZATION_PLANS).map((plan) => (
              <PlanCard key={plan.name} plan={plan} onSignUp={onSignUp} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#ffffff',
        borderTop: '1px solid #e0e0e0',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: 13,
          color: '#999'
        }}>
          © {new Date().getFullYear()} Lausanne Business Solutions. All rights reserved.
        </div>
      </footer>
    </div>
  );
}